import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { contractsAPI } from '../services/api'
import type { Contract } from '../types'

export default function ContractList() {
  const { t } = useTranslation()
  const [contracts, setContracts] = useState<Contract[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  useEffect(() => {
    const params: any = {}
    if (statusFilter) params.status = statusFilter
    if (search) params.q = search
    contractsAPI.list(params).then((resp) => {
      setContracts(resp.data.contracts)
    }).catch(() => {}).finally(() => setLoading(false))
  }, [statusFilter])

  const handleSearch = () => {
    setLoading(true)
    const params: any = {}
    if (statusFilter) params.status = statusFilter
    if (search) params.q = search
    contractsAPI.list(params).then((resp) => {
      setContracts(resp.data.contracts)
    }).finally(() => setLoading(false))
  }

  return (
    <div>
      <div className="page-header">
        <h1><i className="bi bi-file-earmark-text me-2"></i>{t('contract.title')}</h1>
        <Link to="/contracts/new" className="btn btn-primary">
          <i className="bi bi-plus-lg me-1"></i>
          {t('contract.new')}
        </Link>
      </div>

      {/* Filters */}
      <div className="card mb-3">
        <div className="card-body">
          <div className="row g-2 align-items-end">
            <div className="col-md-5">
              <label className="form-label">{t('common.search')}</label>
              <div className="input-group">
                <input
                  type="text"
                  className="form-control"
                  placeholder={t('common.search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button className="btn btn-secondary" onClick={handleSearch}>
                  <i className="bi bi-search"></i>
                </button>
              </div>
            </div>
            <div className="col-md-3">
              <label className="form-label">{t('contract.status')}</label>
              <select className="form-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="">Todos</option>
                <option value="draft">{t('contract.status.draft')}</option>
                <option value="review">{t('contract.status.review')}</option>
                <option value="approved">{t('contract.status.approved')}</option>
                <option value="published">{t('contract.status.published')}</option>
                <option value="signed">{t('contract.status.signed')}</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status" />
            </div>
          ) : contracts.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <i className="bi bi-file-earmark-text display-4 d-block mb-2"></i>
              <p>No hay contratos</p>
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead>
                  <tr>
                    <th>{t('contract.number')}</th>
                    <th>{t('contract.title')}</th>
                    <th>{t('contract.client')}</th>
                    <th>{t('contract.status')}</th>
                    <th>{t('contract.price')}</th>
                    <th>{t('contract.created')}</th>
                    <th className="text-end">Acciones</th>
                  </tr>
                </thead>
                <tbody>
                  {contracts.map((c) => (
                    <tr key={c.id}>
                      <td className="fw-semibold">{c.contract_number}</td>
                      <td>
                        <Link to={`/contracts/${c.id}`} className="text-decoration-none">
                          {c.title}
                        </Link>
                      </td>
                      <td>{c.client_company_name || '—'}</td>
                      <td>
                        <span className={`badge bg-${statusBadge(c.status)}`}>
                          {t(`contract.status.${c.status}`)}
                        </span>
                      </td>
                      <td>{c.price_amount ? `${c.price_amount.toLocaleString()} €` : '—'}</td>
                      <td className="text-muted small">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="text-end">
                        <Link to={`/contracts/${c.id}`} className="btn btn-sm btn-outline-primary me-1">
                          <i className="bi bi-eye"></i>
                        </Link>
                        {c.status === 'draft' && (
                          <Link to={`/contracts/${c.id}/edit`} className="btn btn-sm btn-outline-secondary">
                            <i className="bi bi-pencil"></i>
                          </Link>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function statusBadge(status: string): string {
  const map: Record<string, string> = {
    draft: 'secondary', review: 'warning', approved: 'info',
    published: 'primary', signed: 'success', expired: 'dark', cancelled: 'danger',
  }
  return map[status] || 'secondary'
}
